import { Box, List, ListItemButton, ListItemIcon, ListItemText } from '@mui/material';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import ChatBubbleOutlineRoundedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import RecordVoiceOverRoundedIcon from '@mui/icons-material/RecordVoiceOverRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import LocalActivityOutlinedIcon from '@mui/icons-material/LocalActivityOutlined';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import { NavLink, useLocation } from 'react-router-dom';
import type { LearningModality, SiteLearningWorkspace } from '@common/domain/learning-workspace';
import AccountMenu from '@frontend/components/AccountMenu/AccountMenu';
import { useUserInfoContext } from '@frontend/lib/contexts/UserInfoContext';
import { isSuperAdmin } from '@common/domain/auth';
import { parseModuleOrder, resolveModuleAssignments } from '@frontend/pages/learning-workspace/moduleOrder';
import { config } from '../config';
import './LearningSidebar.css';

const MODALITY_ICON: Record<LearningModality, JSX.Element> = {
  textbook: <MenuBookRoundedIcon />,
  chat: <ChatBubbleOutlineRoundedIcon />,
  avatar: <RecordVoiceOverRoundedIcon />,
};

const MODALITY_LABEL: Record<LearningModality, string> = {
  textbook: 'Textbook',
  chat: 'Chat',
  avatar: 'Avatar',
};

const CUSTOM_TAB_ICON: Record<string, JSX.Element> = {
  about: <InfoOutlinedIcon />,
  activities: <LocalActivityOutlinedIcon />,
};

export interface LearningSidebarProps {
  workspace: SiteLearningWorkspace;
}

export function LearningSidebar({ workspace }: LearningSidebarProps) {
  const { pathname } = useLocation();
  const { userInfo } = useUserInfoContext();

  if (!workspace.enabled || workspace.materials.length === 0) {
    return null;
  }

  // Participants always progress module 1 -> 2 -> 3 in that fixed order; which modality
  // each module uses is randomized per participant (custom:module_order), so tabs are
  // ordered and labeled accordingly rather than always reading Textbook, Chat, Avatar.
  const assignments = resolveModuleAssignments(workspace.materials, parseModuleOrder(userInfo?.moduleOrder));

  return (
    <Box component="nav" className="learning-sidebar" aria-label="Learning modules">
      <List className="learning-sidebar__list" disablePadding>
        {assignments.map(({ modality, chapterSlug, title }) => {
          const to = `/learn/${workspace.persona.slug}/${modality}/${chapterSlug}`;
          const basePath = `/learn/${workspace.persona.slug}/${modality}`;
          // Exact match (not startsWith): a deep link into a *different* chapter of the
          // same modality (e.g. .../avatar/chp6 while this tab points to .../avatar/chp9)
          // must not light up this tab. The bare modality path (no chapter segment) is
          // still treated as active since it's a valid direct route to that modality.
          const isActive = pathname === to || pathname === basePath;
          return (
            <ListItemButton
              key={modality}
              component={NavLink}
              to={to}
              className={`learning-sidebar__item${isActive ? ' is-active' : ''}`}
              title={title}
            >
              <ListItemIcon className="learning-sidebar__icon">
                {MODALITY_ICON[modality]}
              </ListItemIcon>
              <ListItemText primary={MODALITY_LABEL[modality]} />
            </ListItemButton>
          );
        })}
        {(config.customTabs || []).map((tab) => {
          const isActive = pathname === tab.path;
          return (
            <ListItemButton
              key={tab.path}
              component={NavLink}
              to={tab.path}
              className={`learning-sidebar__item${isActive ? ' is-active' : ''}`}
              title={tab.label}
            >
              <ListItemIcon className="learning-sidebar__icon">
                {CUSTOM_TAB_ICON[tab.icon]}
              </ListItemIcon>
              <ListItemText primary={tab.label} />
            </ListItemButton>
          );
        })}
        {isSuperAdmin(userInfo?.groups) && (
          <ListItemButton
            component={NavLink}
            to="/admin/transcripts"
            className={`learning-sidebar__item${pathname.startsWith('/admin') ? ' is-active' : ''}`}
            title="Transcripts"
          >
            <ListItemIcon className="learning-sidebar__icon">
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText primary="Transcripts" />
          </ListItemButton>
        )}
      </List>
      <Box sx={{ flex: 1 }} />
      <AccountMenu showSettings={false} />
    </Box>
  );
}
